/**
 * Profanity detection.
 *
 * Uses `obscenity` rather than a hand-rolled wordlist -- the interesting part
 * of this problem is not the list, it is obfuscation handling and, more
 * importantly, avoiding the Scunthorpe problem. On a paid product a false
 * positive is far more expensive than a miss: the founder is told "no" with no
 * recourse, and simply leaves.
 *
 * Two gaps in the library are patched here, both found by testing rather than
 * assumed:
 *   1. It does not catch single-letter separation (`f-u-c-k`, `f u c k`), so we
 *      also match against a de-spaced copy.
 *   2. Its whitelist misses common surnames and place names -- `Penistone` and
 *      `Dickinson` both match out of the box -- so we redact known-safe words
 *      before matching.
 */
import {
  RegExpMatcher,
  englishDataset,
  englishRecommendedTransformers,
} from 'obscenity'

const matcher = new RegExpMatcher({
  ...englishDataset.build(),
  ...englishRecommendedTransformers,
})

/**
 * Words that legitimately contain a profane substring.
 *
 * Redacted before matching, so the substring inside them is never seen. Bare
 * profanity is unaffected: removing `analytics` does not stop `anal` on its
 * own from matching.
 */
const WHITELIST: readonly string[] = [
  // place names and surnames
  'scunthorpe', 'penistone', 'dickinson', 'dickens', 'dickson', 'cockburn',
  'cockermouth', 'clitheroe', 'lightwater', 'hancock', 'babcock', 'hitchcock',
  'peacock', 'wilcox', 'essex', 'sussex', 'middlesex', 'wessex', 'penisland',
  // ordinary vocabulary
  'analysis', 'analytics', 'analytical', 'analyst', 'analog', 'analogue',
  'canal', 'banal', 'therapist', 'shiitake', 'shitake',
  'assassin', 'assess', 'assessment', 'assign', 'assignment', 'assist',
  'assistant', 'associate', 'association', 'assemble', 'assembly', 'assert',
  'asset', 'assume', 'assumption', 'assure', 'assurance',
  'class', 'classic', 'classify', 'bass', 'mass', 'massive', 'pass', 'passage',
  'grass', 'brass', 'glass', 'compass', 'canvass', 'embassy', 'massachusetts',
  'cumulative', 'accumulate', 'circumstance', 'cumberland', 'document',
  'titan', 'constitution', 'substitute', 'institute', 'button', 'butter',
  'shuttle', 'cocktail', 'cockpit', 'sextant', 'sextet', 'dissect', 'insert',
]

// Longest first, so `analytics` is consumed before a shorter entry could
// partially match inside it.
const WHITELIST_PATTERN = new RegExp(
  [...WHITELIST].sort((a, b) => b.length - a.length).join('|'),
  'gi',
)

function redactWhitelisted(text: string): string {
  return text.replace(WHITELIST_PATTERN, ' ')
}

/** Lowercased with separators removed, to defeat `f-u-c-k` style padding. */
function despace(text: string): string {
  return text.toLowerCase().replace(/[\s\-_.,:;/\\|()[\]{}'"`~*+=]+/g, '')
}

/** True when the text contains profanity, obfuscated or not. */
export function hasProfanity(text: string): boolean {
  const redacted = redactWhitelisted(text)
  if (matcher.hasMatch(redacted)) return true
  // Re-redact after de-spacing: joining words can reconstruct a whitelisted
  // term ("Pen Island" -> "penisland") that was not adjacent before.
  return matcher.hasMatch(redactWhitelisted(despace(redacted)))
}

/**
 * The matched terms, for the review queue and logs.
 *
 * Never surfaced to the user: naming the exact rule that fired turns the error
 * message into an oracle to iterate against.
 */
export function profanityMatches(text: string): readonly string[] {
  const seen = new Set<string>()
  for (const candidate of [redactWhitelisted(text), redactWhitelisted(despace(text))]) {
    for (const match of matcher.getAllMatches(candidate, true)) {
      const meta = englishDataset.getPayloadWithPhraseMetadata(match)
      if (meta.phraseMetadata?.originalWord) seen.add(meta.phraseMetadata.originalWord)
    }
  }
  return [...seen]
}
